<template>
	<nk-wrapper v-if="context" spacing="md">
		<nk-form
			:success="success"
			:error="error"
			:success-message="successMessage"
			:success-description="successDescription"
			:error-message="errorMessage"
			:error-description="errorDescription"
			name="vereinformular"
			:schema="schema"
			@submit="submitForm"
		/>

		<nk-wrapper spacing="md">
			<nk-text v-if="success">
				<nk-button @click.prevent="resetForm">Zurück zum Formular</nk-button>
			</nk-text>
		</nk-wrapper>
	</nk-wrapper>
</template>

<script>
import TemplatesDefault from '../../mixins/templates-default';
import {jumpTo} from '#imports';

export default {
	name: 'NkTemplateFormularVerein',

	mixins: [TemplatesDefault],

	data() {
		const config = useRuntimeConfig();
		return {
			form: null,
			branchen: null,
			error: false,
			locked: false,
			success: false,
			errorMessage:
				'Leider ist ein Fehler beim Eintrag ins Vereinsverzeichnis aufgetreten',
			errorDescription: `Wir werden versuchen den Fehler schnellstmöglich zu
				beheben. Bitte wenden Sie sich in dringenden Fällen direkt
				<a href='mailto:${
					config.public.NUXT_PUBLIC_FORM_RECEIVER_KONTAKT_GEMEINDE ||
					'info@webcontact.de'
				}'>per E-Mail</a>
				an uns.`,
			successMessage: 'Vielen Dank für Ihren Eintrag!',
			successDescription: `Ihre Daten sind bei uns eingegangen. Um unseren Server vor Spam zu
				schützen, wird Ihr Eintrag noch nicht auf der Internetseite angezeigt,
				sondern vorab von einem Mitarbeiter geprüft. Wir bitte Sie um etwas Geduld, sind aber um eine zeitnahe Bearbeitung
				bemüht. Sie erhalten eine Bestätigungsmail mit den eingetragenen Daten an die
				von Ihnen angegebene Mailadresse. Bitte bewahren Sie diese Mail
				sorgfältig auf, Sie enthält einen Link, der es Ihnen ermöglicht,
				Änderungen an Ihrem Eintrag vorzunehmen und so bspw. Tippfehler zu
				korrigieren oder Kontaktdaten anzupassen.
				`,
			genericMail:
				config.public.NUXT_PUBLIC_FORM_RECEIVER_KONTAKT_GEMEINDE ||
				'info@webcontact.de',
		};
	},

	async fetch() {
		this.branchen = await this.$getApiData({
			endpoint: '/api/cms/brancheneintraege_rubrik',
			params: {
				typ: ['vereine'],
				sprache: 'de',
				seite: 1,
				pro_seite: 999,
				order: '`rubrik_details.rubrik` ASC',
				minimal: true,
			},
		});

		if (this.$route.query.token) {
			const brancheneintrag = await this.$getApiData({
				endpoint: `/api/brancheneintrag/${this.$route.query.token}`,
			});

			if (brancheneintrag) {
				this.form = {
					brancheneintraege_id: brancheneintrag.brancheneintraege_id,
					titel: brancheneintrag.titel,
					ansprechpartner: brancheneintrag.ansprechpartner,
					brancheneintraege_rubrik: brancheneintrag.rubriken,
					jugendangebote: brancheneintrag.jugendangebote,
					seniorenangebote: brancheneintrag.seniorenangebote,
					text: brancheneintrag.text,
					oeffnungszeiten: brancheneintrag.oeffnungszeiten,
					bild: brancheneintrag.bild,
					bildtext: brancheneintrag.bildtext,
					strasse: brancheneintrag.adresse.strasse,
					plz: brancheneintrag.adresse.plz,
					ort: brancheneintrag.adresse.ort,
					telefon: brancheneintrag.kontakt.telefon,
					telefax: brancheneintrag.kontakt.telefax,
					mobil: brancheneintrag.kontakt.mobil,
					email: brancheneintrag.kontakt.email,
					link: brancheneintrag.kontakt.link,
					linktext: brancheneintrag.kontakt.linktext,
					seo_title: brancheneintrag.seo.title,
					seo_description: brancheneintrag.seo.description,
				};

				await this.$store.commit(`nuxtKommunen/forms/updateFormField`, {
					name: 'vereinformular',
					form: this.form,
				});
			} else {
				this.success = true;
				this.successMessage =
					'Vielen Dank! Ihr Eintrag ins Vereinsverzeichnis ist momentan noch nicht freigegeben.';
				this.successDescription = `Bitte wenden Sie sich in dringenden Fällen direkt
					<a href='mailto:${this.genericMail}'>per E-Mail</a>
					an uns.`;
			}
		}
	},

	fetchOnServer: false,

	computed: {
		schema() {
			return [
				{
					component: 'h2',
					class: 'text-xl my-4',
					children: 'Angaben zum Verein',
					id: 'headline-1',
				},
				{
					component: 'div',
					class: 'flex flex-wrap -m-2',
					id: 'wrapper-1-1',
					children: [
						{
							component: 'div',
							class: 'w-full sm:w-6/12 p-2',
							id: 'col-1-1-1',
							children: [
								{
									type: 'text',
									label: 'Vereinsname',
									placeholder: 'Musterverein e.V.',
									name: 'titel',
									validation: 'required|min:2',
								},
							],
						},
						{
							component: 'div',
							class: 'w-full sm:w-6/12 p-2',
							id: 'col-1-1-2',
							children: [
								{
									type: 'text',
									label: 'Ansprechpartner',
									placeholder: 'Max Mustermann',
									name: 'ansprechpartner',
									validation: 'required|min:2',
								},
							],
						},
						{
							component: 'div',
							class: 'w-full p-2',
							id: 'col-1-1-3',
							children: [
								{
									type: 'multiselect',
									label: 'Rubriken',
									placeholder: 'Rubrik wählen...',
									name: 'brancheneintraege_rubrik',
									options: this.branchen ? this.branchen.data : null,
									validation: 'required',
									'validation-messages': {
										required:
											'Bitte wählen Sie mindestens eine Rubrik aus der Liste',
									},
									'data-option-track-by': 'id',
									'data-option-labelled-by': 'rubrik',
									'data-multiple': true,
								},
							],
						},
						{
							component: 'div',
							class: 'w-full p-2',
							id: 'col-1-1-5',
							children: [
								{
									type: 'wysiwyg',
									label: 'Beschreibung des Vereins',
									placeholder: 'Beschreibung des Vereins ...',
									name: 'text',
									validation: 'required',
								},
							],
						},
						{
							component: 'div',
							class: 'w-full p-2',
							id: 'col-1-1-6',
							children: [
								{
									type: 'wysiwyg',
									label: 'Angebote für Kinder & Jugendliche (optional)',
									name: 'jugendangebote',
								},
							],
						},
						{
							component: 'div',
							class: 'w-full p-2',
							id: 'col-1-1-7',
							children: [
								{
									type: 'wysiwyg',
									label: 'Angebote für Senioren (optional)',
									name: 'seniorenangebote',
								},
							],
						},
						{
							component: 'div',
							class: 'w-full p-2',
							id: 'col-1-1-8',
							children: [
								{
									type: 'wysiwyg',
									label: 'Öffnungszeiten / Sprechzeiten (optional)',
									name: 'text',
									validation: 'required',
								},
							],
						},
						{
							component: 'div',
							class: 'w-full p-2',
							id: 'col-1-1-9',
							children: [
								{
									type: 'customfile',
									label: 'Bild / Vereinswappen (optional)',
									name: 'bild',
									accept: 'image/jpeg,image/png',
									'data-formname': 'vereinformular',
								},
							],
						},
						{
							component: 'div',
							class: 'w-full p-2',
							id: 'col-1-1-10',
							children: [
								{
									type: 'text',
									label: 'Bildtext (optional)',
									placeholder: 'Vereinswappen Musterverein',
									name: 'bildtext',
								},
							],
						},
					],
				},
				{
					component: 'h2',
					class: 'text-xl my-4',
					children: 'Adressangaben',
					id: 'headline-2',
				},
				{
					component: 'div',
					class: 'flex flex-wrap -m-2',
					id: 'wrapper-2',
					children: [
						{
							component: 'div',
							class: 'w-full p-2',
							id: 'col-2-1',
							children: [
								{
									type: 'text',
									label: 'Straße',
									placeholder: 'Musterstraße 11',
									name: 'strasse',
									validation: 'required|min:2',
								},
							],
						},
						{
							component: 'div',
							class: 'w-full sm:w-3/12 p-2',
							id: 'col-2-2',
							children: [
								{
									type: 'text',
									label: 'PLZ',
									placeholder: '12345',
									name: 'plz',
									validation: 'required|min:2',
								},
							],
						},
						{
							component: 'div',
							class: 'w-full sm:flex-1 p-2',
							id: 'col-2-3',
							children: [
								{
									type: 'text',
									label: 'Ort',
									placeholder: 'Musterstadt',
									name: 'ort',
									validation: 'required|min:2',
								},
							],
						},
					],
				},
				{
					component: 'h2',
					class: 'text-xl my-4',
					children: 'Kontakt',
					id: 'headline-3',
				},
				{
					component: 'div',
					class: 'flex flex-wrap -m-2',
					id: 'wrapper-3',
					children: [
						{
							component: 'div',
							class: 'w-full sm:w-6/12 p-2',
							id: 'col-3-1',
							children: [
								{
									type: 'tel',
									label: 'Telefonnummer',
									placeholder: '+49 0123 4567',
									name: 'telefon',
									validation: 'required|min:2',
								},
							],
						},
						{
							component: 'div',
							class: 'w-full sm:w-6/12 p-2',
							id: 'col-3-2',
							children: [
								{
									type: 'tel',
									label: 'Fax (optional)',
									placeholder: '+49 0123 4567',
									name: 'telefax',
								},
							],
						},
						{
							component: 'div',
							class: 'w-full sm:w-6/12 p-2',
							id: 'col-3-3',
							children: [
								{
									type: 'tel',
									label: 'Mobil (optional)',
									placeholder: '+49 0123 4567',
									name: 'mobil',
								},
							],
						},
						{
							component: 'div',
							class: 'w-full sm:w-6/12 p-2',
							id: 'col-3-4',
							children: [
								{
									type: 'email',
									label: 'E-Mail Adresse',
									placeholder: 'max.mustermann@beispiel.de',
									name: 'email',
									validation: 'required|email',
								},
							],
						},
						{
							component: 'div',
							class: 'w-full sm:w-6/12 p-2',
							id: 'col-3-5',
							children: [
								{
									type: 'url',
									label: 'Webseite (optional)',
									placeholder: 'https://www.beispiel.de',
									name: 'link',
								},
							],
						},
						{
							component: 'div',
							class: 'w-full sm:w-6/12 p-2',
							id: 'col-3-6',
							children: [
								{
									type: 'text',
									label: 'Linktext für die Webseite (optional)',
									placeholder: 'Webseite der Musterfirma',
									name: 'linktext',
								},
							],
						},
					],
				},
				{
					component: 'h2',
					class: 'text-xl my-4',
					children: 'Angaben für Suchmaschinen',
					id: 'headline-4',
				},
				{
					component: 'div',
					class: 'flex flex-wrap -m-2',
					id: 'wrapper-4',
					children: [
						{
							component: 'div',
							class: 'w-full sm:w-6/12 p-2',
							id: 'col-4-1',
							children: [
								{
									type: 'text',
									label: 'Seitentitel (optional)',
									name: 'seo_title',
								},
							],
						},
						{
							component: 'div',
							class: 'w-full sm:w-6/12 p-2',
							id: 'col-4-2',
							children: [
								{
									type: 'text',
									label: 'Beschreibung der Seite (optional)',
									name: 'seo_description',
								},
							],
						},
					],
				},
				{
					component: 'div',
					children: [
						{
							type: 'text',
							label: '',
							class: 'opacity-0 absolute top-0 left-0 h-0 w-0 z-0',
							placeholder: 'Bitte leer lassen, dieses Feld ist nur für Roboter',
							tabIndex: '-1',
							autocomplete: 'off',
							name: 'firma',
						},
					],
				},
				{
					component: 'h2',
					class: 'text-xl my-4',
					children: 'Datenschutzerklärung',
					id: 'headline-5',
				},
				{
					component: 'div',
					class: 'flex flex-wrap -m-2',
					id: 'wrapper-5',
					children: [
						{
							component: 'div',
							class: 'w-full p-2',
							id: 'col-5-1',
							children: [
								{
									type: 'consent',
									consent: `Ja, ich habe die <a href="${this.privacyLink()}" target="_blank" rel="noopener">Datenschutz­erklärung</a> zur Kenntnis genommen und bin damit einverstanden, dass die von mir angegebenen Daten elektronisch erhoben und gespeichert werden. Meine Daten werden dabei nur streng zweckgebunden zur Bearbeitung und Beantwortung meiner Anfrage genutzt. Ich kann meine Einwilligung jederzeit per E-Mail an <a href="mailto:${this.privacyEmail()}">${this.privacyEmail()}</a> widerrufen.`,
									name: 'datenschutz',
									class: 'text-sm',
									validation: 'required',
								},
							],
						},
					],
				},
				{
					component: 'hr',
					class: '',
				},
				{
					component: 'div',
					class: 'flex flex-wrap -m-2',
					id: 'wrapper-6',
					children: [
						{
							component: 'div',
							class: 'w-auto  p-2',
							id: 'col-6-1',
							children: [
								{
									type: 'submit',
									name: 'Anfrage abschicken',
								},
							],
						},
					],
				},
			];
		},
	},

	methods: {
		resetForm() {
			this.form = null;
			this.error = false;
			this.success = false;

			window.setTimeout(() => {
				this.$el.querySelector('[name="bild"]').value = '';
				const firstInput = this.$el.querySelector('input, select, button');

				jumpTo(firstInput);

				firstInput.focus();
			}, 250);
		},

		async submitForm(form) {
			this.form = form;

			if (this.form.firma && this.form.firma.length) {
				this.error = true;
				return false;
			}

			try {
				const formData = new FormData();

				formData.append(
					'brancheneintraege_id',
					this.form.brancheneintraege_id ? this.form.brancheneintraege_id : 0,
				);
				formData.append('titel', this.form.titel);
				formData.append('ansprechpartner', this.form.ansprechpartner);
				formData.append(
					'brancheneintraege_rubrik_typ',
					this.branchen ? this.branchen.data[0].typ : 'gewerbebetriebe',
				);
				this.form.brancheneintraege_rubrik.forEach(branchenrubrikDetail => {
					formData.append(
						'brancheneintraege_rubrik[]',
						branchenrubrikDetail.id,
					);
				});
				formData.append('jugendangebote', this.form.jugendangebote);
				formData.append('seniorenangebote', this.form.seniorenangebote);
				formData.append('text', this.form.text);
				formData.append('oeffnungszeiten', this.form.oeffnungszeiten);
				if (this.$el.querySelector('[name="bild"]').files.length) {
					formData.append(
						'bild',
						this.$el.querySelector('[name="bild"]').files[0],
					);
				}
				formData.append('bildtext', this.form.bildtext);
				formData.append('strasse', this.form.strasse);
				formData.append('plz', this.form.plz);
				formData.append('ort', this.form.ort);
				formData.append('telefon', this.form.telefon);
				formData.append('telefax', this.form.telefax);
				formData.append('mobil', this.form.mobil);
				formData.append('email', this.form.email);
				formData.append('link', this.form.link);
				formData.append('linktext', this.form.linktext);
				formData.append('seo_title', this.form.seo_title);
				formData.append('seo_description', this.form.seo_description);

				formData.append('route', this.$route.path);

				const mailResult = await this.$axios.$post(
					'/api/brancheneintrag',
					formData,
					{
						headers: {'Content-Type': 'multipart/form-data'},
					},
				);

				if (!mailResult) {
					this.error = true;
					return false;
				}

				this.error = false;
				this.success = true;
				return true;
			} catch (err) {
				if (this.$sentry) {
					this.$sentry.captureException(err);
				}

				this.error = true;
				return false;
			}
		},

		privacyLink() {
			return this.options.privacyLink || '/datenschutz';
		},

		privacyEmail() {
			const config = useRuntimeConfig();
			return (
				this.options.privacyEmail ||
				config.public.NUXT_PUBLIC_FORM_RECEIVER_BRANCHENEINTRAG
			);
		},
	},
};
</script>
